import { AlertCircle } from "lucide-react";

import { ErrorBanner } from "../components/ErrorBanner";
import { Skeleton } from "../components/Skeleton";
import type { StockRow } from "../lib/api";
import { useStock } from "../lib/queries";
import { colors, fonts, radius, shadow, tokens } from "../styles/theme";

const LOW_STOCK_DAYS = 14;
const WARN_STOCK_DAYS = 28;

export function InventoryView() {
  const stock = useStock();

  if (stock.isLoading) return <Skeleton count={4} height={120} />;
  if (stock.isError)
    return <ErrorBanner message={`Failed to load stock: ${(stock.error as Error).message}`} />;

  const items = stock.data ?? [];

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "12px",
        }}
      >
        {items.map((item) => (
          <StockRowCard key={item.id} item={item} />
        ))}
        {items.length === 0 && (
          <div
            style={{
              padding: "40px 20px",
              background: tokens.card,
              border: `1px solid ${tokens.line}`,
              borderRadius: radius.lg,
              color: tokens.muted,
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            No seed stock on file.
          </div>
        )}
      </div>
    </div>
  );
}

function StockRowCard({ item }: { item: StockRow }) {
  const aged = item.germ_date !== null && new Date(item.germ_date) < new Date("2024-01-01");
  const lowGerm = item.germ_pct !== null && item.germ_pct < 85;
  const daysLeft = item.burn_down.days_until_empty;
  const low = daysLeft !== null && daysLeft < LOW_STOCK_DAYS;
  const warn = !low && daysLeft !== null && daysLeft < WARN_STOCK_DAYS;

  return (
    <div
      style={{
        padding: "18px",
        background: tokens.card,
        border: `1px solid ${tokens.line}`,
        borderRadius: radius.lg,
        boxShadow: shadow.xs,
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "12px",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: "20px",
              color: tokens.ink,
              lineHeight: 1.1,
            }}
          >
            {item.crop}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: tokens.muted,
              marginTop: "2px",
            }}
          >
            {[item.source, item.size].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        {(aged || lowGerm) && (
          <span
            style={{
              display: "inline-flex",
              gap: "4px",
              alignItems: "center",
              fontSize: "10px",
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              padding: "3px 8px",
              background: tokens.amberSoft,
              color: tokens.amberInk,
              borderRadius: radius.pill,
              whiteSpace: "nowrap",
            }}
          >
            <AlertCircle size={12} />
            {aged && "Aged"}
            {aged && lowGerm && " · "}
            {lowGerm && `Germ ${item.germ_pct}%`}
          </span>
        )}
      </div>

      {item.notes && (
        <div
          style={{
            fontSize: "13px",
            color: tokens.inkSoft,
            lineHeight: 1.5,
          }}
        >
          {item.notes}
        </div>
      )}

      <BurnDownBar item={item} low={low} warn={warn} />

      <div
        style={{
          display: "flex",
          gap: "14px",
          alignItems: "center",
          flexWrap: "wrap",
          fontSize: "12px",
        }}
      >
        {item.remaining_oz !== null && (
          <span
            style={{
              fontFamily: fonts.mono,
              color: tokens.inkSoft,
              fontWeight: 500,
            }}
          >
            {item.remaining_oz.toFixed(2)} oz remaining
          </span>
        )}
        <DaysLeftLabel daysLeft={daysLeft} low={low} warn={warn} />
      </div>
    </div>
  );
}

function BurnDownBar({ item, low, warn }: { item: StockRow; low: boolean; warn: boolean }) {
  if (item.size_oz === null || item.remaining_oz === null) return null;
  const remaining = Math.max(0, item.remaining_oz);
  const size = Math.max(remaining, item.size_oz || 0);
  const pct = size > 0 ? Math.min(100, (remaining / size) * 100) : 0;
  const fill = low ? tokens.coral : warn ? tokens.amber : tokens.sage;
  return (
    <div
      role="progressbar"
      aria-label={`${item.crop} remaining seed`}
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{
        height: "8px",
        background: tokens.sunken,
        borderRadius: radius.pill,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          width: `${pct}%`,
          background: fill,
          borderRadius: radius.pill,
          transition: "width 0.35s ease",
        }}
      />
    </div>
  );
}

function DaysLeftLabel({
  daysLeft,
  low,
  warn,
}: {
  daysLeft: number | null;
  low: boolean;
  warn: boolean;
}) {
  if (daysLeft === null) {
    return (
      <span
        data-testid="burn-down-label"
        style={{
          fontFamily: fonts.mono,
          color: tokens.muted,
          fontWeight: 500,
        }}
      >
        Insufficient data
      </span>
    );
  }
  // colors.infrared is the backward-compatible alias for coral — tests
  // reference it directly. Keeping the low-stock color binding via the
  // legacy name preserves existing assertions.
  const color = low ? colors.infrared : warn ? tokens.amber : tokens.inkSoft;
  return (
    <span
      data-testid="burn-down-label"
      style={{
        fontFamily: fonts.mono,
        color,
        fontWeight: low ? 600 : 500,
      }}
    >
      ~{daysLeft} days left
    </span>
  );
}
